import { useState, useEffect, useRef, useCallback } from "react";
import io from "socket.io-client";
import { buildApiUrl } from "../../../services/apiConfig";
import { getUserID } from "../../auth/services/authService";
import { downsampleBuffer, floatTo16BitPCM } from "../components/AudioProcessor";

export const useTranscription = (onTranscriptionComplete) => {
  const [isRecording, setIsRecording] = useState(false);
  const [hasStartedRecording, setHasStartedRecording] = useState(false);
  const [transcription, setTranscription] = useState("");
  const [persistentLines, setPersistentLines] = useState([]);
  const [uploadStatus, setUploadStatus] = useState("");

  const transcriptionEndRef = useRef(null);
  const transcriptionTextRef = useRef("");
  const audioContextRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const processorRef = useRef(null);
  const intervalRef = useRef(null);
  const socketRef = useRef(null);
  const fileCountRef = useRef(0);

  const socketUrl = buildApiUrl('SOCKET');
  const RAG_UPLOAD_URL = buildApiUrl('UPLOAD', '/upload-documents');

  useEffect(() => {
    socketRef.current = io(socketUrl, {
      path: '/socket.io/',
      transports: ["websocket", "polling"],
      reconnectionAttempts: 5
    });

    const socket = socketRef.current;

    socket.on("transcription", (data) => {
      if (!data || !data.text) return;

      transcriptionTextRef.current += `\n${data.text}`;

      if (data.text.startsWith("##")) {
        setPersistentLines((prev) => [...prev, data.text]);
        setTranscription("");
      } else if (data.text.startsWith(">>")) {
        setTranscription(data.text);
      }
    });

    socket.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
    });

    return () => {
      socket.disconnect();
    };
  }, [socketUrl]);

  const uploadTranscription = useCallback(async (text) => {
    if (!text.trim()) {
      return;
    }

    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `transcription_${fileCountRef.current++}_${timestamp}.txt`;
      const file = new File([text], fileName, { type: 'text/plain' });

      const formData = new FormData();
      formData.append('files', file);
      formData.append('collection_name', getUserID());

      await fetch(RAG_UPLOAD_URL, {
        method: 'POST',
        body: formData
      });

      setTimeout(() => setUploadStatus(""), 3000);
    } catch (error) {
      console.error("Error uploading transcription:", error);
      setUploadStatus("");
    }
  }, [RAG_UPLOAD_URL]);

  const handleStartRecording = useCallback(async () => {
    if (isRecording) return;

    setIsRecording(true);
    setHasStartedRecording(true);
    setUploadStatus("");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      await audioContext.audioWorklet.addModule('/audio-processor.js');

      const source = audioContext.createMediaStreamSource(stream);
      const workletNode = new AudioWorkletNode(audioContext, 'audio-processor');

      workletNode.port.onmessage = (event) => {
        try {
          const inputData = event.data.audioData;
          const originalSampleRate = audioContext.sampleRate;
          const targetSampleRate = 16000;
          const downsampled = downsampleBuffer(inputData, originalSampleRate, targetSampleRate);
          const pcmBuffer = floatTo16BitPCM(downsampled);
          const binaryData = new Uint8Array(pcmBuffer);

          socketRef.current.emit("audio_stream", binaryData);
        } catch (err) {
          console.error("Error during audio processing:", err);
        }
      };

      source.connect(workletNode);
      workletNode.connect(audioContext.destination);
      processorRef.current = workletNode;
    } catch (error) {
      console.error("Error accessing microphone:", error);
      setIsRecording(false);
    }

    intervalRef.current = setInterval(() => {
      const currentText = transcriptionTextRef.current + (transcription || "");

      if (currentText.trim()) {
        socketRef.current.emit("context_update", { text: currentText });
      }

      if (onTranscriptionComplete) {
        onTranscriptionComplete(currentText);
      }
    }, 3000);
  }, [isRecording, onTranscriptionComplete, transcription]);

  const handleStopRecording = useCallback(() => {
    if (!isRecording) return;

    setIsRecording(false);

    if (transcription.trim()) {
      transcriptionTextRef.current += `\n${transcription}`;
    }

    const finalText = transcriptionTextRef.current;

    if (onTranscriptionComplete) {
      onTranscriptionComplete(finalText);
    }

    uploadTranscription(finalText);
    transcriptionTextRef.current = "";

    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current.port.postMessage({ terminate: true });
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
    }

    if (audioContextRef.current) {
      audioContextRef.current.close().then(() => console.log("Audio context closed"));
    }

    socketRef.current.emit("stop_audio");

    clearInterval(intervalRef.current);
    intervalRef.current = null;
  }, [isRecording, onTranscriptionComplete, transcription, uploadTranscription]);

  return {
    isRecording,
    hasStartedRecording,
    transcription,
    persistentLines,
    uploadStatus,
    handleStartRecording,
    handleStopRecording,
    transcriptionEndRef
  };
};